// client/src/hooks/useFranquiaPlans.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import useAlert from './useAlert';

const FRANQUIA_PLANS_QUERY_KEY = ['franquiaPlans'];
const SUBSCRIPTIONS_FOR_FRANQUIA_PLAN_QUERY_KEY = ['subscriptionsForFranquiaPlan'];

export const useFranquiaPlans = (params, options = {}) => {
    return useQuery({
        queryKey: [FRANQUIA_PLANS_QUERY_KEY, params],
        queryFn: async () => {
            const { data } = await apiClient.get('/plans-franquias', { params });
            return data.data;
        },
        ...options 
    });
};

export const useFranquiaPlanById = (id) => {
    return useQuery({
        queryKey: [FRANQUIA_PLANS_QUERY_KEY, id],
        queryFn: async () => {
            if (!id) return null;
            const { data } = await apiClient.get(`/plans-franquias/${id}`);
            return data.data;
        },
        enabled: !!id,
    });
};

export const useSubscriptionsForFranquiaPlan = (planId) => {
    return useQuery({
        queryKey: [SUBSCRIPTIONS_FOR_FRANQUIA_PLAN_QUERY_KEY, planId],
        queryFn: async () => {
            // A rota real no backend é /inscricoes (plansFranquiasRoutes.js),
            // não /subscriptions — esse mismatch fazia a aba "Inscrições" do
            // formulário sempre receber 404 e ficar vazia.
            const { data } = await apiClient.get(`/plans-franquias/${planId}/inscricoes`);
            return data.data;
        },
        enabled: !!planId,
    });
};

export const useMutateFranquiaPlans = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateCache = () => {
        queryClient.invalidateQueries({ queryKey: [FRANQUIA_PLANS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS_FOR_FRANQUIA_PLAN_QUERY_KEY] });
    };

    const createMutation = useMutation({
        mutationFn: (planData) => apiClient.post('/plans-franquias', planData),
        onSuccess: () => {
            invalidateCache();
            setAlert('Plano de franquia criado com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao criar plano de franquia.', 'danger'),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, planData }) => apiClient.put(`/plans-franquias/${id}`, planData),
        onSuccess: (data, variables) => {
            invalidateCache();
            queryClient.invalidateQueries({ queryKey: [FRANQUIA_PLANS_QUERY_KEY, variables.id] });
            setAlert('Plano de franquia atualizado com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao atualizar plano de franquia.', 'danger'),
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => {
            if (window.confirm('Tem certeza que deseja desativar este plano de franquia? A ação não pode ser desfeita.')) {
                return apiClient.delete(`/plans-franquias/${id}`);
            }
            return Promise.reject(new Error('Ação cancelada'));
        },
        onSuccess: () => {
            invalidateCache();
            setAlert('Plano de franquia desativado com sucesso.', 'success');
        },
        onError: (err) => {
            if (err.message !== 'Ação cancelada') {
                setAlert(err.response?.data?.error || 'Erro ao desativar plano de franquia.', 'danger');
            }
        },
    });

    return { 
        createMutation,
        updateMutation,
        deleteMutation,
        createPlan: createMutation.mutateAsync,
        updatePlan: updateMutation.mutateAsync,
        deletePlan: deleteMutation.mutateAsync,
        isPending: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
        error: createMutation.error || updateMutation.error || deleteMutation.error,
    };
};